import React, { useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, X, Trash2, ShieldAlert, Flag, FlagOff } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import PlayChecklist from "@/components/admin/PlayChecklist";
import { fetchCountries } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Stars, photoSrc } from "@/components/CountryReviews";
import AdminStats from "@/components/AdminStats";
import AdminReply from "@/components/AdminReply";

const API = process.env.REACT_APP_BACKEND_URL;
const TABS = ["pending", "approved", "rejected", "reported", "play"];

export default function Admin() {
  const { lang, t } = useLang();
  const { user, loading, login } = useAuth();
  const { data: countries = [] } = useQuery({ queryKey: ["countries"], queryFn: fetchCountries });
  const [params] = useSearchParams();
  const [tab, setTab] = useState(TABS.includes(params.get("tab")) ? params.get("tab") : "pending");
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    const r = await fetch(`${API}/api/admin/reviews?status=${tab}`, { credentials: "include" });
    setItems(r.ok ? await r.json() : []);
  }, [tab]);

  useEffect(() => { if (user?.is_admin) load(); }, [user, load]);

  const act = async (id, method, status) => {
    await fetch(`${API}/api/admin/reviews/${id}`, {
      method,
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: status ? JSON.stringify({ status }) : undefined,
    });
    load();
  };

  const clearReports = async (id) => { await fetch(`${API}/api/admin/reviews/${id}/reports`, { method: "DELETE", credentials: "include" }); load(); };

  const onReplySaved = (id, admin_reply) => setItems((list) => list.map((r) => (r.id === id ? { ...r, admin_reply } : r)));

  if (loading) return <div className="max-w-3xl mx-auto px-4 py-16 text-center text-muted-foreground">…</div>;
  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="admin-login-required">
        <button onClick={login} className="px-5 py-2.5 rounded-full bg-ink text-sand text-sm hover:bg-ochre transition-colors" data-testid="admin-login">{t("review_login")}</button>
      </div>
    );
  }
  if (!user.is_admin) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center text-muted-foreground inline-flex items-center justify-center gap-2 w-full" data-testid="admin-forbidden">
        <ShieldAlert className="w-5 h-5" /> {t("admin_forbidden")}
      </div>
    );
  }

  const name = (code) => { const c = countries.find((x) => x.code === code); return c ? c.short_names[lang] : code; };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-10" data-testid="admin-page">
      <h1 className="text-4xl sm:text-5xl font-display font-bold text-ink tracking-tight mb-6">{t("admin_title")}</h1>
      <AdminStats />
      <div className="flex gap-2 mb-6">
        {TABS.map((s) => (
          <button key={s} onClick={() => setTab(s)} className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${tab === s ? "bg-ink text-sand border-ink" : "border-stoneLine hover:bg-sandDeep/50"}`} data-testid={`admin-tab-${s}`}>
            {t(`status_${s}`)}
          </button>
        ))}
      </div>
      {tab === "play" && <PlayChecklist />}
      {tab !== "play" && items.length === 0 && <p className="text-sm text-muted-foreground" data-testid="admin-empty">{t("admin_empty")}</p>}
      <ul className="space-y-3">
        {items.map((r) => (
          <li key={r.id} className="rounded-2xl border border-stoneLine bg-card p-4" data-testid={`admin-review-${r.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-ochre font-semibold">{name(r.country_code)}</div>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className="text-sm font-semibold">{r.author.name}</span>
                  <span className="text-xs text-muted-foreground">{r.author_email}</span>
                  <Stars value={r.rating} size="w-3.5 h-3.5" />
                  <span className="text-[10px] uppercase text-muted-foreground">{r.lang}</span>
                  {r.report_count > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-red-50 text-red-700 px-2 py-0.5 text-[10px] font-semibold" data-testid={`report-count-${r.id}`}><Flag className="w-3 h-3" /> {r.report_count}{r.suspended_by_reports ? ` · ${t("admin_suspended")}` : ""} · {t(`status_${r.status}`)}</span>}
                </div>
              </div>
              <div className="flex gap-1.5">
                {r.report_count > 0 && <Btn onClick={() => clearReports(r.id)} cls="bg-sandDeep/50 text-ink hover:bg-oasis/20" testid={`clear-reports-${r.id}`} title={t("admin_reports_clear")}><FlagOff className="w-4 h-4" /></Btn>}
                {(tab !== "approved" && r.status !== "approved") && <Btn onClick={() => act(r.id, "PATCH", "approved")} cls="bg-oasis text-white hover:bg-oasis/80" testid={`approve-${r.id}`}><Check className="w-4 h-4" /></Btn>}
                {(tab !== "rejected" && r.status !== "rejected") && <Btn onClick={() => act(r.id, "PATCH", "rejected")} cls="bg-ochre/15 text-ochre hover:bg-ochre/30" testid={`reject-${r.id}`}><X className="w-4 h-4" /></Btn>}
                <Btn onClick={() => act(r.id, "DELETE")} cls="bg-sandDeep/50 text-ink hover:bg-red-100 hover:text-red-700" testid={`delete-${r.id}`}><Trash2 className="w-4 h-4" /></Btn>
              </div>
            </div>
            <p className="text-sm text-ink/85 mt-3" dir="auto">{r.text}</p>
            {r.video_url && <video src={photoSrc(r.video_url)} poster={photoSrc(r.video_poster_url)} controls preload="metadata" playsInline className="mt-2 h-32 rounded-lg border border-stoneLine bg-black" data-testid={`admin-video-${r.id}`} />}
            {r.photo_url && <a href={photoSrc(r.photo_url)} target="_blank" rel="noopener noreferrer"><img src={photoSrc(r.photo_url)} alt="" className="mt-2 h-28 rounded-lg border border-stoneLine object-cover" data-testid={`admin-photo-${r.id}`} /></a>}
            {tab === "approved" && <AdminReply key={`${r.id}-${r.admin_reply?.at || ""}`} review={r} onSaved={onReplySaved} />}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Btn({ onClick, cls, children, testid, title }) {
  return <button onClick={onClick} title={title} className={`w-9 h-9 rounded-full inline-flex items-center justify-center transition-colors ${cls}`} data-testid={testid}>{children}</button>;
}
